import { PrivateAxiosInstance } from '@/api/axios'
import { type IResponse, type IResponseById } from '@/interface/response.interface'
import { isAxiosError } from 'axios'

export interface IGetParameter {
  perPage?: number
  page?: number
  search?: string
}
type IGets = Record<string, string>

const appendFormValue = (formData: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return
  if (value instanceof File) {
    formData.append(key, value)
    return
  }
  if (Array.isArray(value) && value.every((item) => item instanceof File)) {
    value.forEach((file) => formData.append(key, file))
    return
  }
  if (typeof value === 'object') {
    formData.append(key, JSON.stringify(value))
    return
  }
  formData.append(key, String(value))
}

const useAPI = <T>() => {
  const get = async (endPoint: string, args?: IGets): Promise<IResponse<T>> => {
    try {
      const url = new URL(`${import.meta.env.VITE_API_URL}${endPoint}`)
      if (args) {
        const searchParams = new URLSearchParams(url.search)
        for (const [key, value] of Object.entries(args)) {
          if (value) searchParams.set(key, value)
        }
        url.search = searchParams.toString()
      }

      const response = await PrivateAxiosInstance.get(url.toString())
      return {
        status: true,
        data: response?.data?.data,
        message: 'Fetched',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) {
            message = 'Too many requests. Please try again later.'
          }
        }

      return {
        status: false,
        data: null,
        message,
      }
    }
  }

  const getOne = async (endPoint: string, args?: IGets): Promise<IResponseById<T>> => {
    try {
      const url = new URL(`${import.meta.env.VITE_API_URL}${endPoint}`)
      if (args) {
        const searchParams = new URLSearchParams(url.search)
        for (const [key, value] of Object.entries(args)) {
          if (value !== null && value !== undefined) {
            searchParams.set(key, value)
          }
        }
        url.search = searchParams.toString()
      }

      const response = await PrivateAxiosInstance.get(url.toString())
      return {
        status: true,
        data: response?.data?.data,
        message: 'Fetched',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) {
            message = 'Too many requests. Please try again later.'
          }
        }

      return {
        status: false,
        data: null,
        message,
      }
    }
  }

  const getById = async (endPoint: string, id: string, args?: IGets): Promise<IResponseById<T>> => {
    try {
      const url = new URL(`${import.meta.env.VITE_API_URL}${endPoint}/${id}`)
      if (args) {
        const searchParams = new URLSearchParams(url.search)
        for (const [key, value] of Object.entries(args)) {
          if (value !== null && value !== undefined) {
            searchParams.set(key, value)
          }
        }
        url.search = searchParams.toString()
      }

      const response = await PrivateAxiosInstance.get(url.toString())
      return {
        status: true,
        data: response?.data?.data,
        message: 'Fetch successfully',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) {
            message = 'Too many requests. Please try again later.'
          }
        }

      return {
        status: false,
        data: null,
        message,
      }
    }
  }

  const post = async (
    endPoint: string,
    data: Partial<T>,
    contentType: 'application/json' | 'multipart/form-data' = 'application/json'
  ) => {
    try {
      const config = {
        headers: {
          'Content-Type': contentType,
        },
      }

      let response: IResponse<T>

      if (contentType === 'multipart/form-data') {
        const formData = new FormData()
        for (const [key, value] of Object.entries(data)) {
          appendFormValue(formData, key, value)
        }
        response = await PrivateAxiosInstance.post(endPoint, formData, config)
      } else {
        response = await PrivateAxiosInstance.post(endPoint, data, config)
      }

      return {
        status: true,
        data: response?.data?.data,
        message: (response?.data as unknown as { message: string })?.message || 'Created',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) {
            message = 'Too many requests. Please try again later.'
          }
        }
      return {
        status: false,
        message,
      }
    }
  }

  const patch = async (
    endPoint: string,
    data: Partial<T>,
    contentType: 'application/json' | 'multipart/form-data' = 'application/json'
  ) => {
    try {
      const config = {
        headers: {
          'Content-Type': contentType,
        },
      }

      let response: IResponse<T>

      if (contentType === 'multipart/form-data') {
        const formData = new FormData()
        for (const [key, value] of Object.entries(data)) {
          appendFormValue(formData, key, value)
        }
        response = await PrivateAxiosInstance.patch(endPoint, formData, config)
      } else {
        response = await PrivateAxiosInstance.patch(endPoint, data, config)
      }
      return {
        status: true,
        data: response?.data?.data,
        message: 'Updated',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) {
            message = 'Too many requests. Please try again later.'
          }
        }

      return {
        status: false,
        message,
      }
    }
  }

  const remove = async (
    endPoint: string,
    id: string
  ): Promise<{
    status: boolean
    message: string
  }> => {
    try {
      await PrivateAxiosInstance.delete(`${endPoint}/${id}`)
      return {
        status: true,
        message: 'Deleted successfully',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) {
            message = 'Too many requests. Please try again later.'
          }
        }

      return {
        status: false,
        message,
      }
    }
  }

  const removeAll = async (
    endPoint: string
  ): Promise<{
    status: boolean
    message: string
  }> => {
    try {
      await PrivateAxiosInstance.delete(endPoint)
      return {
        status: true,
        message: 'Deleted successfully',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) message = 'Too many requests. Please try again later.'
        }

      return {
        status: false,
        message,
      }
    }
  }

  const removeMultiple = async (
    endPoint: string,
    ids: string[]
  ): Promise<{
    status: boolean
    message: string
  }> => {
    try {
      await PrivateAxiosInstance.delete(`${endPoint}`, { data: { ids } })
      return {
        status: true,
        message: 'Deleted successfully',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) {
            message = 'Too many requests. Please try again later.'
          }
        }

      return {
        status: false,
        message,
      }
    }
  }

  const downloadFile = async (endPoint: string, args?: IGets, defaultFilename?: string): Promise<IResponseById<T>> => {
    try {
      const url = new URL(`${import.meta.env.VITE_API_URL}${endPoint}`)
      if (args) {
        const searchParams = new URLSearchParams(url.search)
        for (const [key, value] of Object.entries(args)) {
          if (value !== null && value !== undefined) searchParams.set(key, value)
        }
        url.search = searchParams.toString()
      }

      const response = await PrivateAxiosInstance.get(url.toString(), {
        responseType: 'blob',
      })

      // Extract filename from Content-Disposition header
      let filename = defaultFilename || 'download'
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
        if (filenameMatch) filename = filenameMatch[1]
      }

      // Create blob URL and trigger download (response.data is already a Blob when responseType is 'blob')
      const blobUrl = window.URL.createObjectURL(response.data)

      // Create temporary link and trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)

      return {
        status: true,
        data: null,
        message: 'File downloaded successfully',
      }
    } catch (error: unknown) {
      let message = 'An error occurred'

      if (isAxiosError(error))
        if (error?.response) {
          message = error?.response?.data?.message || message
          if (error.response.status === 429) message = 'Too many requests. Please try again later.'
        }

      return {
        status: false,
        data: null,
        message,
      }
    }
  }

  return { get, getById, post, patch, remove, removeAll, getOne, downloadFile, removeMultiple }
}

export default useAPI
